import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faPlay,
  faStop,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './CreativeScheduleHistory.css';

/**
 * CreativeScheduleHistory - Modal to display execution history for a creative schedule
 */
const CreativeScheduleHistory = ({ isOpen, onClose, schedule, campaign, accountId, user }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0 });

  useEffect(() => {
    if (isOpen && schedule?.id) {
      fetchLogs();
    }
  }, [isOpen, schedule?.id]);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules/${schedule.id}/logs`),
        { params: { accountId, userId: user?.userId || user?.id, limit: 50 } }
      );
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || {});
    } catch (err) {
      console.error('Error fetching execution logs:', err);
      setError('Failed to load execution logs');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (createdAt, completedAt) => {
    if (!createdAt || !completedAt) return '-';
    const duration = new Date(completedAt) - new Date(createdAt);
    const seconds = Math.floor(duration / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusBadge = (status) => {
    const config = {
      completed: { icon: faCheckCircle, color: '#10b981', label: 'Completed' },
      processing: { icon: faSpinner, color: '#6366f1', label: 'Processing', spin: true },
      failed: { icon: faExclamationCircle, color: '#ef4444', label: 'Failed' }
    };
    const { icon, color, label, spin } = config[status] || { icon: faExclamationCircle, color: '#9ca3af', label: status };
    return (
      <span className="status-badge" style={{ color }}>
        <FontAwesomeIcon icon={icon} spin={spin} /> {label}
      </span>
    );
  };

  const getActionBadge = (action) => {
    if (action === 'enable') {
      return <span className="action-badge enable"><FontAwesomeIcon icon={faPlay} /> Enable</span>;
    }
    return <span className="action-badge disable"><FontAwesomeIcon icon={faStop} /> Disable</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="execution-history-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faHistory} /> Execution History
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-subtitle">
          {schedule?.adName || schedule?.adId} • {pagination.total} total executions
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-state">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading history...
            </div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : logs.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faHistory} size="2x" style={{ color: '#9ca3af', marginBottom: '12px' }} />
              <p>No executions yet</p>
            </div>
          ) : (
            <div className="executions-table-container">
              <table className="executions-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Action</th>
                    <th>Triggered By</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} className={log.status === 'failed' ? 'failed-row' : ''}>
                      <td>{formatDateTime(log.created_at)}</td>
                      <td>{getActionBadge(log.action)}</td>
                      <td>{log.triggered_by === 'immediate' ? (log.user_name || 'User') : 'Scheduler'}</td>
                      <td>{formatDuration(log.created_at, log.completed_at)}</td>
                      <td>{getStatusBadge(log.status)}</td>
                      <td>
                        {log.error_message && (
                          <span className="error-message" title={log.error_message}>
                            {log.error_message.substring(0, 50)}...
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreativeScheduleHistory;

